import type { AgentClarificationRequest } from '../../../../ekko-agent/src'

export type EkkoClarificationResolutionReason =
  | 'response'
  | 'timeout'
  | 'aborted'
  | 'cancelled'
  | 'unavailable'

export interface EkkoClarificationResolution {
  response: string
  reason: EkkoClarificationResolutionReason
}

interface PendingEkkoClarification {
  sessionId: string
  resolve: (response: string) => void
  timer: NodeJS.Timeout
  signal?: AbortSignal
  onAbort?: () => void
  onResolved?: (resolution: EkkoClarificationResolution) => void
}

export interface WaitForEkkoClarificationOptions {
  sessionId: string
  signal?: AbortSignal
  onRequested: (request: AgentClarificationRequest) => void
  onResolved?: (resolution: EkkoClarificationResolution) => void
}

export interface EkkoClarificationResponse {
  handled: boolean
  resolved: boolean
}

const pendingClarifications = new Map<string, PendingEkkoClarification>()

export function waitForEkkoClarification(
  request: AgentClarificationRequest,
  options: WaitForEkkoClarificationOptions,
): Promise<string> {
  if (options.signal?.aborted) {
    return Promise.resolve('[clarification cancelled because the run was aborted]')
  }
  if (
    pendingClarifications.has(request.clarifyId) ||
    [...pendingClarifications.values()].some(pending => pending.sessionId === options.sessionId)
  ) {
    return Promise.resolve('[another clarification is already pending for this session]')
  }

  return new Promise((resolve) => {
    const finish = (resolution: EkkoClarificationResolution) => {
      const pending = pendingClarifications.get(request.clarifyId)
      if (!pending) return
      pendingClarifications.delete(request.clarifyId)
      clearTimeout(pending.timer)
      if (pending.signal && pending.onAbort) {
        pending.signal.removeEventListener('abort', pending.onAbort)
      }
      pending.onResolved?.(resolution)
      pending.resolve(resolution.response)
    }
    const timer = setTimeout(() => finish({
      response: '[user did not respond within 5m]',
      reason: 'timeout',
    }), request.timeoutMs)
    timer.unref?.()
    const onAbort = () => finish({
      response: '[clarification cancelled because the run was aborted]',
      reason: 'aborted',
    })
    pendingClarifications.set(request.clarifyId, {
      sessionId: options.sessionId,
      resolve,
      timer,
      signal: options.signal,
      onAbort,
      onResolved: options.onResolved,
    })
    options.signal?.addEventListener('abort', onAbort, { once: true })
    try {
      options.onRequested(request)
    } catch {
      finish({
        response: '[clarification could not be shown to the user]',
        reason: 'unavailable',
      })
    }
  })
}

export function respondToEkkoClarification(
  sessionId: string,
  clarifyId: string,
  response: unknown,
): EkkoClarificationResponse {
  const pending = pendingClarifications.get(clarifyId)
  if (!pending) return { handled: false, resolved: false }
  if (pending.sessionId !== sessionId) return { handled: true, resolved: false }

  pendingClarifications.delete(clarifyId)
  clearTimeout(pending.timer)
  if (pending.signal && pending.onAbort) {
    pending.signal.removeEventListener('abort', pending.onAbort)
  }
  const normalizedResponse = typeof response === 'string' ? response : String(response ?? '')
  pending.onResolved?.({
    response: normalizedResponse,
    reason: 'response',
  })
  pending.resolve(normalizedResponse)
  return { handled: true, resolved: true }
}

export function cancelPendingEkkoClarifications(sessionId?: string): number {
  const targets = [...pendingClarifications.entries()]
    .filter(([, pending]) => !sessionId || pending.sessionId === sessionId)
  for (const [clarifyId, pending] of targets) {
    pendingClarifications.delete(clarifyId)
    clearTimeout(pending.timer)
    if (pending.signal && pending.onAbort) {
      pending.signal.removeEventListener('abort', pending.onAbort)
    }
    const resolution: EkkoClarificationResolution = {
      response: '[clarification cancelled because the runtime is shutting down]',
      reason: 'cancelled',
    }
    pending.onResolved?.(resolution)
    pending.resolve(resolution.response)
  }
  return targets.length
}
